import React, { useState } from "react";
import { CommentType, ReplyContentType } from "./CommentTypes";
import { Comment } from "./Comment";
import SkeletonComment from "./SkeletonComment";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { CommentsBlockContainer } from "./CommentsBlock.styles";
import CommentOptionsModal from "../Modals/ServiceModals/CommentOptionsModal/CommentOptionsModal";
import { CommentOptionsModalType } from "../Modals/ServiceModals/ServiceModal.types";
import { SHOW_AVATAR_MODAL_EVENT } from "../Modals/ServiceModals/AvatarModal";
import { UserType } from "../../Shared/types";
import { SERVICE_MODALS_OPTIONS_STATES } from "../Modals/ServiceFunctionsModal.types";
import {
    fetcherReactToComment,
    fetcherUnReactToComment,
} from "../../DataFetchers/CommentDataFetcher";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "../../../Infrastructure/Persistence/UserState";
import {
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "../../../Infrastructure/BackendClientCodes";
import {
    ErrorState,
    useErrorStore,
} from "../Modals/ServiceModals/GeneralErrorsModal/GlobalState";
import { AxiosError } from "axios";
import { PostCommentsInfo } from "../../../Infrastructure/Services/PostCommentsService.types";
import { EntityReactionType } from "../../Shared/Types/Reactions/ReactionTypes.types";
import {
    ReactionActionState,
    SWITCHING_STATE_TO_IDLE_TIME,
    useReactionActionStore,
} from "../ReactionsTooltip/ReactionActionState";
import { publish } from "../../../Infrastructure/Events/EventBus";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "../../../Infrastructure/Services/ApiUtility";
import {
    deleteUserReactionOnObject,
    addUserReactionToObject,
} from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";

dayjs.locale("de");
export default function PinnedCommentsBlock({
    onDeleteSuspendSuccess,
    onEdit,
    pinnedComment,
    setReplyComment,
    updatePinnedComment,
    onRepliedCommentClick,
    isLatestComment,
}: {
    onDeleteSuspendSuccess: (response: PostCommentsInfo) => void;
    onEdit: (comment: CommentType) => void;
    onRepliedCommentClick: (id: number) => void;
    pinnedComment: CommentType;
    setReplyComment: (item: ReplyContentType) => void;
    updatePinnedComment: (pinnedComment: CommentType) => void;
    isLatestComment: boolean;
}) {
    const { setErrorState } = useErrorStore();
    const { setUserAuthContextState } = useUserStore();
    const { setReactionActionState } = useReactionActionStore();

    const [selectedComment, setSelectedComment] = useState({} as CommentType);

    const [openCommentOptionsModal, setOpenCommentOptionsModal] =
        useState(false);

    const [modalCurrentAction, setModalCurrentAction] = useState(
        SERVICE_MODALS_OPTIONS_STATES.COMMENT_STARTING_OPTIONS,
    );

    const [actionInProgress, setActionInProgress] = useState<{
        action: string;
        commentId: string;
        reactionTypeName: string;
    }>();

    const cancelActionInProgress = () => {
        setActionInProgress(undefined);
    };

    useInterruptableAction(
        actionInProgress,
        () =>
            performReactionOnComment(
                actionInProgress?.reactionTypeName,
                actionInProgress?.action,
            ),
        cancelActionInProgress,
    );

    function handleEditingComment(comment: CommentType) {
        onEdit(comment);
        setOpenCommentOptionsModal(false);
    }

    function handleReplyComment(comment: CommentType) {
        const replyObjectForCurrentComment: ReplyContentType = {
            id: comment.commentContent.id,
            text: comment.commentContent.text,
            images: comment.commentContent.images,
            user: comment.commentContent.user,
        };
        setReplyComment(replyObjectForCurrentComment);
        setOpenCommentOptionsModal(false);
    }

    const createReactOnCommentFunction =
        () => (reactionTypeName: string, action: string) => {
            resetIgnoreApiCacheTime(
                RESET_API_CACHE_REASONS.REACT_ON_PINNED_COMMENT,
            );
            setReactionActionState(ReactionActionState.LOADING);
            setActionInProgress({
                reactionTypeName: reactionTypeName,
                action: action,
                commentId: commentId,
            });
            setUserAuthContextState(UserLoginActionState.CREATE_REACTION);
            performReactionOnComment(reactionTypeName, action);
        };

    const performReactionOnComment = (
        reactionTypeName?: string,
        action?: string,
    ) => {
        if (!reactionTypeName || !action) {
            return;
        }

        const catchError = (err: AxiosError) => {
            if (
                err.response?.status === RESPONSE_CODE_RESOURCE_SUSPENDED ||
                err.response?.status === RESPONSE_CODE_RESOURCE_NOT_FOUND
            ) {
                setErrorState(ErrorState.POST_SUSPENDED);
                setTimeout(
                    () => setReactionActionState(ReactionActionState.IDLE),
                    SWITCHING_STATE_TO_IDLE_TIME,
                );
            }
        };

        switch (action) {
            case "create":
                fetcherReactToComment(commentId, reactionTypeName)
                    .then((res) => {
                        pinnedComment.commentReactions = res.reactions;
                        updatePinnedComment(pinnedComment);
                        addUserReactionToObject(
                            commentId,
                            reactionTypeName,
                            ReactionableObjectEnum.COMMENT,
                        );
                        setActionInProgress(undefined);
                        setTimeout(
                            () =>
                                setReactionActionState(
                                    ReactionActionState.IDLE,
                                ),
                            SWITCHING_STATE_TO_IDLE_TIME,
                        );
                    })
                    .catch(catchError);
                break;
            case "delete":
                fetcherUnReactToComment(commentId, reactionTypeName)
                    .then((res) => {
                        pinnedComment.commentReactions = res.reactions;
                        deleteUserReactionOnObject(
                            commentId,
                            ReactionableObjectEnum.COMMENT,
                        );
                        setActionInProgress(undefined);
                        setTimeout(
                            () =>
                                setReactionActionState(
                                    ReactionActionState.IDLE,
                                ),
                            SWITCHING_STATE_TO_IDLE_TIME,
                        );
                    })
                    .catch(catchError);
                break;
        }
    };

    if (!pinnedComment || !pinnedComment.commentContent.id) {
        return null;
    }

    const commentId = pinnedComment.commentContent.id.toString();

    const commentOptionsModalProps: CommentOptionsModalType = {
        open: openCommentOptionsModal,
        setOpen: setOpenCommentOptionsModal,
        selectedComment: selectedComment,
        onCommentReplyOptionClickHandler: handleReplyComment,
        handleEditingComment: handleEditingComment,
        onCommentDeleteSuspendSuccess: onDeleteSuspendSuccess,
        modalCurrentAction: modalCurrentAction,
        setModalCurrentAction: setModalCurrentAction,
        createReactOnCommentFunction: createReactOnCommentFunction,
        isLatestComment: isLatestComment,
    };

    function handleSelectingComment(comment: CommentType) {
        setSelectedComment(comment);
        setModalCurrentAction(
            SERVICE_MODALS_OPTIONS_STATES.COMMENT_STARTING_OPTIONS,
        );
        setOpenCommentOptionsModal(true);
    }

    function handleSelectingCommentUser(user: UserType) {
        publish({ name: SHOW_AVATAR_MODAL_EVENT, user: user });
    }

    function handleReportingComment(comment: CommentType) {
        setSelectedComment(comment);
        setModalCurrentAction(SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT);
        setOpenCommentOptionsModal(true);
    }

    const commentOpts = {
        comment: pinnedComment,
        showCommentAvatarOptionsModal: handleSelectingCommentUser,
        handleReplyComment,
        handleSelectingComment,
        createReactOnCommentFunction,
        handleReportingComment: handleReportingComment,
        isPinnedComment: true,
        onRepliedCommentClick,
    };

    return (
        <CommentsBlockContainer>
            {selectedComment?.commentContent != null && (
                <CommentOptionsModal {...commentOptionsModalProps} />
            )}
            {pinnedComment === undefined && <SkeletonComment />}
            {pinnedComment && <Comment {...commentOpts} />}
        </CommentsBlockContainer>
    );
}
